// Manual "Record GPS now" action.
//
// Independent of the active GPS watch / preset. Reads one fresh fix via
// Capacitor Geolocation and pushes it through the same sendOrQueuePing
// pipeline so it benefits from the offline IndexedDB outbox automatically.
//
// Does NOT touch the active watch — no restart, no preset change, no queue
// clear. Tagged source="manual_gps" so it's distinguishable from automatic
// preset-driven points.
//
// Robustness layers (in order):
//   1. If the active watch already produced a fresh fix (< 90 s old, accuracy
//      <= 50 m), reuse it instantly. Tagged "manual_gps_recent". Avoids
//      asking iOS for a cold fix when we already have a perfectly good one.
//   2. Cold fix path is wrapped in a hard outer Promise.race timeout so the
//      caller's spinner can never hang past ~12 s, regardless of Capacitor's
//      own (unreliable on iOS) `timeout` option behavior.
//   3. If high-accuracy times out, fall back to a low-accuracy attempt
//      (network/cell triangulation) with a shorter race timeout. Total worst
//      case ~20 s before we surface a clean failure.

import { getCurrentPing, getCurrentPingLowAccuracy } from "./geolocation";
import { sendOrQueuePing } from "./ping-outbox";
import { requestLocationPermission } from "@/lib/native";
import { markGpsFix, getGpsStatus } from "./gps-status";

export type ManualGpsResult =
  | {
      ok: true;
      status: "sent" | "queued";
      lat: number;
      lng: number;
      accuracy: number | null;
      source: "manual_gps" | "manual_gps_recent" | "manual_gps_low_accuracy";
      ageMs?: number; // present when source === "manual_gps_recent"
    }
  | { ok: false; reason: string };

const RECENT_FIX_MAX_AGE_MS = 90_000;
const RECENT_FIX_MAX_ACCURACY_M = 50;
const HIGH_ACC_TIMEOUT_MS = 12_000;
const LOW_ACC_TIMEOUT_MS = 8_000;
/** Reject high-accuracy fixes worse than this — manual recordings must be trustworthy. */
const MANUAL_MAX_HIGH_ACC_M = 100;
/** Even the network-triangulation fallback must be inside this bound. */
const MANUAL_MAX_LOW_ACC_M = 250;

function raceWithTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

export async function recordManualGpsNow(tripId: string): Promise<ManualGpsResult> {
  console.info("[manual-gps] requested");

  // Permission gate. Re-using the same helper as Start tracking so the prompt
  // path is identical.
  let perm: string;
  try {
    perm = await requestLocationPermission();
  } catch (e: any) {
    console.info(`[manual-gps] permission=denied`);
    console.info(`[manual-gps] failed reason=permission-error`);
    return { ok: false, reason: e?.message ?? "Location permission unavailable." };
  }
  if (perm === "denied") {
    console.info(`[manual-gps] permission=denied`);
    console.info(`[manual-gps] failed reason=permission-denied`);
    return { ok: false, reason: "Location permission denied." };
  }
  console.info(`[manual-gps] permission=granted`);

  // ---------- Layer 1: recent-fix shortcut ----------
  const status = getGpsStatus();
  if (
    status.lastFixAt != null &&
    status.lastLat != null &&
    status.lastLng != null &&
    Date.now() - status.lastFixAt <= RECENT_FIX_MAX_AGE_MS &&
    (status.lastAccuracy == null || status.lastAccuracy <= RECENT_FIX_MAX_ACCURACY_M)
  ) {
    const ageMs = Date.now() - status.lastFixAt;
    console.info(
      `[manual-gps] reusing recent watch fix age=${ageMs}ms acc=${status.lastAccuracy ?? "null"}`,
    );
    const recent = await pushPing({
      tripId,
      lat: status.lastLat,
      lng: status.lastLng,
      accuracy: status.lastAccuracy,
      altitude: null,
      speed: null,
      heading: null,
      source: "manual_gps_recent",
    });
    if (recent.ok) {
      return { ...recent, source: "manual_gps_recent", ageMs };
    }
    // Push failed — fall through to fresh acquisition.
  }

  // ---------- Layer 2: high-accuracy cold fix with hard timeout ----------
  let ping: Awaited<ReturnType<typeof getCurrentPing>> | null = null;
  let acquiredSource: "manual_gps" | "manual_gps_low_accuracy" = "manual_gps";
  try {
    ping = await raceWithTimeout(getCurrentPing(), HIGH_ACC_TIMEOUT_MS, "high-accuracy");
  } catch (e: any) {
    console.info(`[manual-gps] high-accuracy failed reason=${e?.message ?? "unknown"}`);
    // ---------- Layer 3: low-accuracy fallback ----------
    try {
      ping = await raceWithTimeout(
        getCurrentPingLowAccuracy(LOW_ACC_TIMEOUT_MS),
        LOW_ACC_TIMEOUT_MS + 1_000,
        "low-accuracy",
      );
      acquiredSource = "manual_gps_low_accuracy";
      console.info(`[manual-gps] low-accuracy succeeded`);
    } catch (e2: any) {
      const reason = e2?.message ?? "no fix";
      console.info(`[manual-gps] low-accuracy failed reason=${reason}`);
      return { ok: false, reason: "timeout" };
    }
  }

  if (!ping) return { ok: false, reason: "no fix" };

  // Weak-fix guard. Manual recordings must reflect a trustworthy location —
  // never persist a 1000m+ accuracy "ghost" point as if the user were there.
  const acc = ping.accuracy ?? null;
  const ceiling = acquiredSource === "manual_gps_low_accuracy"
    ? MANUAL_MAX_LOW_ACC_M
    : MANUAL_MAX_HIGH_ACC_M;
  if (acc == null || !Number.isFinite(acc) || acc > ceiling) {
    console.info(`[manual-gps] reject weak-fix acc=${acc ?? "null"}m ceiling=${ceiling}m source=${acquiredSource}`);
    return { ok: false, reason: "weak-fix" };
  }

  // Surface in the OfflineTrackingStatus panel immediately.
  try { markGpsFix(ping.lat, ping.lng, acc, Date.now()); } catch { /* ignore */ }

  const sent = await pushPing({
    tripId,
    lat: ping.lat,
    lng: ping.lng,
    accuracy: acc,
    altitude: ping.altitude ?? null,
    speed: ping.speed ?? null,
    heading: ping.heading ?? null,
    source: acquiredSource,
  });
  if (!sent.ok) return sent;
  return { ...sent, source: acquiredSource };
}

async function pushPing(args: {
  tripId: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  source: "manual_gps" | "manual_gps_recent" | "manual_gps_low_accuracy";
}): Promise<
  | { ok: true; status: "sent" | "queued"; lat: number; lng: number; accuracy: number | null }
  | { ok: false; reason: string }
> {
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  try {
    const status = await sendOrQueuePing({
      tripId: args.tripId,
      lat: args.lat,
      lng: args.lng,
      accuracy: args.accuracy,
      altitude: args.altitude,
      speed: args.speed,
      heading: args.heading,
      capturedAt: new Date().toISOString(),
      // Outbox schema only accepts "manual_gps" in this enum; richer source
      // (recent / low_accuracy) is preserved in the returned ManualGpsResult
      // for UX (toast wording, analytics) and in console logs.
      source: "manual_gps",
      clientPointKey: `manual:${(globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)}`,
    });
    console.info(
      `[manual-gps] saved lat=${args.lat} lng=${args.lng} accuracy=${args.accuracy ?? "null"} online=${online} source=${args.source} status=${status}`,
    );
    return { ok: true, status, lat: args.lat, lng: args.lng, accuracy: args.accuracy };
  } catch (e: any) {
    const reason = e?.message ?? "send/queue failed";
    console.info(`[manual-gps] failed reason=${reason}`);
    return { ok: false, reason };
  }
}
